import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import {
    HasProductContext,
    ProductContext,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import { FormInputWrapper } from "@insite/mobius/FormField";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridItemStyle } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import ProductBuyNowButton from "@sunSource/Components/ProductBuyNowButton";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
}

type Props = OwnProps &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps>;

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}
export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    buyNowButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productImageWrapper?: InjectableCss;
    productDescWrapper?: InjectableCss;
    productDescWrapperFirstDiv?: InjectableCss;
    productDescWrapperSecondDiv?: InjectableCss;
    productDescWrapperThirdDiv?: InjectableCss;
    productPriceCarouselStyles?: ProductPriceStyles;
    productDetailsActionStyles?: InjectableCss;
    pricingMainWrapper?: InjectableCss;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    quoteMessage?: RequiresQuoteMessageStyle;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productImageStyles: {
        img: {
            css: css`
                height: unset;
                width: unset;
                max-height: 250px;
                margin: auto;
            `,
        },
    },
    pricingMainWrapper: {
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            > section[class^="sc"] {
                width: auto;
            }
        `,
    },
    productDetailsActionStyles: {
        css: css`
            display: flex;
            flex-direction: column;
            padding-bottom: 10px;
            > ${TypographyStyle} {
                > ${TypographyStyle} {
                    font-weight: 600;
                }
            }
        `,
    },
    productPriceCarouselStyles: {
        price: {
            unitOfMeasure: {
                unitOfMeasureText: {
                    css: css`
                        white-space: break-spaces;
                    `,
                },
            },
        },
    },
    productBrandWrapper: {
        css: css`
            width: 100%;
            margin: 6px 0;
            min-height: 22px;
            > ${StyledButton} {
                &:hover {
                    text-decoration: underline;
                }
                > ${TypographyStyle} {
                    font-size: 1rem;
                    font-weight: 500;
                    color: ${getColor("secondary")};
                    &:hover {
                        color: ${getColor("secondary")};
                    }
                }
            }
        `,
    },
    productDescWrapper: {
        css: css`
            padding: 1rem;
            background-color: ${getColor("common.background")};
            height: 100%;
            display: flex;
            flex-wrap: wrap;
            > div {
                width: 100%;
            }
        `,
    },
    productDescWrapperFirstDiv: {
        css: css`
            // height: 60%;
        `,
    },
    productDescWrapperThirdDiv: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: auto;
        `,
    },
    productImageWrapper: {
        css: css`
            width: 100%;
            background-color: ${getColor("common.background")};
            min-height: 250px;
            > ${StyledButton} {
                justify-content: center;
            }
        `,
    },
    productDescriptionStyles: {
        productDetailLink: {
            color: "secondary",
            css: css`
                margin-bottom: 6px;
                min-height: 48px;
                align-items: start;
                &:hover {
                    text-decoration: underline;
                }
                > ${TypographyStyle} {
                    font-size: 1rem;
                    font-weight: 500;
                }
            `,
        },
    },
    productPartNumbersStyles: {
        container: {
            css: css`
                flex-grow: 0;
                margin-bottom: 6px;
            `,
        },
        erpNumberLabelText: { size: 13 },
        erpNumberValueText: { size: 14 },
    },
    productPriceWrapper: {
        css: css`
            flex-grow: 1;
            color: ${getColor("secondary")};
            font-size: 16px;
            margin-bottom: 5px;
            > div[class^="sc"] {
                > ${TypographyStyle} {
                    font-weight: 500;
                }
            }
        `,
    },
    productUnitOfMeasureSelectStyles: {
        cssOverrides: {
            formField: css`
                margin-top: 6px;
            `,
        },
    },
    addToCartContainer: {
        css: css`
            flex-grow: 0;
            // padding: 0px 1rem;
            > ${GridOffset} {
                width: 100%;
                margin: 0px;
                > ${GridItemStyle} {
                    padding: 0px;
                }
            }
        `,
    },
    productQuantityOrderedStyles: {
        cssOverrides: {
            formField: css`
                margin: 0px 10px 0px 0px;
                > ${FormInputWrapper} {
                    input {
                        height: 40px !important;
                        border: 1px solid ${getColor("common.border")};
                    }
                }
            `,
        },
    },
    qtyGridItem: {
        width: [3, 3, 4, 5, 3],
        css: css`
            background: ${getColor("common.background")};
            align-items: center;
        `,
    },
    addToCartButtonGridItem: { width: [9, 9, 8, 7, 9] },
    buyNowButtonGridItem: {
        width: 12,
        css: css`
            justify-content: center;
        `,
    },
    productAddToCartButtonStyles: {
        css: css`
            width: 100%;
        `,
    },
    productAddToListLinkStyles: {
        link: {
            typographyProps: { size: 16 },
            css: css`
                justify-content: center;
                padding: 0.5rem;
                font-weight: 600;
            `,
        },
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    quoteMessage: {
            text: { weight: "bold", size: "14px" },
            tooltip: {
                iconProps: { size: 14 },
                cssOverrides: {
                    tooltipWrapper: css`
                        white-space: normal;
                        z-index: 2;
                    `,
                    tooltipContainer: css`
                        transform: translateX(-100%);
                    `,
                },
            },
        },
};

const quoteStyles = productCarouselProductStyles.quoteMessage || {};

const ProductCarouselProduct: React.FC<Props> = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    const productListingPrice = productContext.productInfo.pricing?.unitRegularPrice;
    const productQuoteRequired = productContext.product.quoteRequired;
    const productActionsSiteMessage = siteMessage("ProductActions_SiteMessage");
    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                {showImage && !showOverlayBadge && (
                    <ProductImage
                        product={productContext}
                        onClick={() => trackProductRecommendation(true)}
                        extendedStyles={styles.productImageStyles}
                    />
                )}
                {showImage && showOverlayBadge && overlayBadge && (
                    <StyledWrapper
                        {...styles.badgeAreaWrapper}
                        ref={badgeAreaWrapperRef}
                        data-test-selector="productCarouselBadge"
                    >
                        <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                        <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                            <Badge
                                type={overlayBadge.badgeType}
                                value={
                                    overlayBadge.badgeType === "Image"
                                        ? overlayBadge.largeImageBadgePath
                                        : overlayBadge.displayText
                                }
                                badgeTextColor={overlayBadge.textColorHexCode}
                                badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                imageAltText={overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name}
                                textBadgeCss={styles.badgeText}
                                imageBadgeProps={styles.badgeImageProps}
                            />
                        </StyledWrapper>
                    </StyledWrapper>
                )}
                <StyledWrapper {...styles.productDescWrapper}>
                    <StyledWrapper {...styles.productDescWrapperFirstDiv}>
                        {showBrand && product.brand && (
                            <StyledWrapper {...styles.productBrandWrapper}>
                                <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                            </StyledWrapper>
                        )}
                        {showTitle && (
                            <ProductDescription
                                product={productContext}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper {...styles.productDescWrapperSecondDiv}>
                        {showPartNumbers && (
                            <ProductPartNumbers
                                productNumber={product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={product.manufacturerItem}
                                showCustomerName={false}
                                showManufacturerItem={false}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                    </StyledWrapper>
                    {productListingPrice! < 1 && productQuoteRequired === false ? (
                        <>
                            <StyledWrapper {...styles.productDetailsActionStyles}>
                                <StyledWrapper style={{display:"flex", alignItems:"center", gap:"5px"}}>
                                    {translate("Price:")}
                                        <StyledWrapper  style={{display:"flex", alignItems:"center", gap:"1px"}}>
                                            <Typography {...quoteStyles.text}>
                                                Requires Quote
                                            </Typography>
                                            <Tooltip
                                                {...quoteStyles.tooltip}
                                                text={siteMessage("Rfq_TooltipMessage").toString()}
                                            />
                                        </StyledWrapper>
                                </StyledWrapper>
                                <Typography>{productActionsSiteMessage}</Typography>
                            </StyledWrapper>
                            {showAddToCart && (
                                <GridContainer {...styles.addToCartContainer}>
                                    <GridItem {...styles.qtyGridItem}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${carouselId}-qty`}
                                            id={`${carouselId}-qty-label`}
                                        >
                                            {translate("QTY")}
                                        </VisuallyHidden>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            extendedStyles={styles.productQuantityOrderedStyles}
                                            id={`${carouselId}-qty`}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.addToCartButtonGridItem}>
                                        <ProductAddToCartButton
                                            onAddToCartClick={() => trackProductRecommendation(false)}
                                            data-test-selector="addToCartBtn"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.buyNowButtonGridItem}>
                                        <ProductBuyNowButton
                                            onAddToCartClick={() => trackProductRecommendation(false)}
                                            data-test-selector="buyNowButton"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.buyNowButtonGridItem}>
                                        <ProductAddToListLink
                                            data-test-selector="addToListLink"
                                            extendedStyles={styles.productAddToListLinkStyles}
                                        />
                                    </GridItem>
                                </GridContainer>
                            )}
                        </>
                    ) : (
                        <StyledWrapper {...styles.productDescWrapperThirdDiv}>
                            {showPrice && (
                                <StyledWrapper {...styles.productPriceWrapper}>
                                    <VisuallyHidden
                                        as="label"
                                        htmlFor={`${carouselId}-uom`}
                                        id={`${carouselId}-uom-label`}
                                    >
                                        {translate("U/M")}
                                    </VisuallyHidden>
                                    <StyledWrapper {...styles.pricingMainWrapper}>
                                        <ProductPrice
                                            showLabel={false}
                                            product={productContext}
                                            extendedStyles={styles.productPriceCarouselStyles}
                                        />
                                        {product.unitOfMeasures &&
                                            // <ProductUnitOfMeasureSelect
                                            //     labelOverride=""
                                            //     extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                            //     id={`${carouselId}-uom`}
                                            // />
                                            null}
                                        <ProductQuantityBreakPricing
                                            extendedStyles={styles.productQuantityBreakPricingStyles}
                                        />
                                    </StyledWrapper>
                                </StyledWrapper>
                            )}
                            {showAddToCart && (
                                <GridContainer {...styles.addToCartContainer}>
                                    <GridItem {...styles.qtyGridItem}>
                                        <VisuallyHidden
                                            as="label"
                                            htmlFor={`${carouselId}-qty`}
                                            id={`${carouselId}-qty-label`}
                                        >
                                            {translate("QTY")}
                                        </VisuallyHidden>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            extendedStyles={styles.productQuantityOrderedStyles}
                                            id={`${carouselId}-qty`}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.addToCartButtonGridItem}>
                                        <ProductAddToCartButton
                                            onAddToCartClick={() => trackProductRecommendation(false)}
                                            data-test-selector="addToCartBtn"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.buyNowButtonGridItem}>
                                        <ProductBuyNowButton
                                            onAddToCartClick={() => trackProductRecommendation(false)}
                                            data-test-selector="buyNowButton"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                        />
                                    </GridItem>
                                </GridContainer>
                            )}
                            {showAddToList && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                />
                            )}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </ProductContext.Provider>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductCarouselProduct));
